import React, { useState } from 'react';
import { motion } from 'framer-motion';


export default function Portfolio() {
  const projects = [
  {
    title: "Cafe",
    description: "React website for a café (responsive + modern UI).",
    tech: "React • CSS • Responsive Design",
    outcome: "Live site + mobile-ready layout",
    image: "/Cafe.webp",
    imagePosition: "left bottom",
    link: "https://wrapperscafe.com/"
  },
  {
    title: "Beauty Salon",
    description: "Frontend website built with vanilla code (clean + fast).",
    tech: "HTML • CSS • JavaScript",
    outcome: "Responsive pages + smooth UX",
    image: "/Renee.webp",
    imagePosition: "center center",
    link: "https://renee-beauty.netlify.app/"
  },
  {
  title: "Cyphra",
  description: "Full-stack platform in development — frontend, backend, database, and auth.",
  tech: "React • API • Database • Auth • Security",
  outcome: "FULL-STACK • IN PROGRESS",
  image: "/Cyphra.webp",
  imagePosition: "center",
  disabled: true
  }

];


  const services = [
  { title: "Business website (3–7 days)", icon: "◆", price: "From £750" },
  { title: "Website redesign", icon: "◆", price: "From £550" },
  { title: "Landing page", icon: "◆", price: "From £350" }
];

  return (
    <div className="portfolio-container">
      {/* Fixed Background Image */}
      <div className="bg-image" />

      {/* Animated Red Clouds */}
      <div className="animated-clouds" />

      {/* Dark Overlay for Readability */}
      <div className="dark-overlay" />

      {/* Hero Section */}
      <section className="hero-section">
        <motion.div
          className="hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <motion.h1 
            className="hero-title"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            Shaya <span className="separator">—</span> <span className="craft-text">Digital Craftsman</span>
          </motion.h1>

          <motion.div 
            className="hero-buttons"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <button
              className="btn-primary"
              type="button"
              onClick={() => {
                document.getElementById("work")?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              <span>VIEW WORK</span>
            </button>

            <button
              className="btn-secondary"
              type="button"
              onClick={() => {
                document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              <span>CONTACT</span>
            </button>
          </motion.div>
        </motion.div>
      </section>

      {/* About Section */}
      <section className="about-section">
        <motion.div 
          className="about-container"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          <motion.div
            className="about-label"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            [ WHO AM I ]
          </motion.div>
          
          <motion.h2 
            className="about-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="blood-text">About</span>
          </motion.h2>

          <motion.div
            className="about-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <p>
              A frontend developer crafting <strong>fast, modern websites</strong> for businesses that want to stand out.
            </p>
            <p>
              I design and build responsive sites that look sharp, load fast, and work perfectly on every device. 
              From clean landing pages to full business websites, I focus on turning ideas into smooth, reliable digital experiences.
            </p>
            <p>
              If you care about <strong>quality, speed, and clear communication</strong>, we'll work well together.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Projects Section */}
      <section className="projects-section" id="work">
        <motion.div
          className="section-label"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          [ RECENT CONQUESTS ]
        </motion.div>

        <motion.h2 
          className="section-title"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          WORK
        </motion.h2>

        <div className="projects-grid">
          {projects.map((project, index) => (
            <ProjectCard key={index} project={project} index={index} />
          ))}
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <motion.div
          className="section-label"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          [ WHAT I OFFER ]
        </motion.div>

        <motion.h2 
          className="section-title"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          SERVICES
        </motion.h2>

        <div className="services-grid">
          {services.map((service, index) => (
            <motion.div
              key={index}
              className="service-card"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: index * 0.15 }}
              whileHover={{ scale: 1.05, y: -10 }}
            >
              <div className="service-icon">{service.icon}</div>
              <h3>{service.title}</h3>
              <div className="service-price">{service.price}</div>
              <div className="service-slash" />
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section" id="contact">
        <motion.div
          className="section-label"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          [ MAKE CONTACT ]
        </motion.div>

        <motion.h2 
          className="section-title"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          LET'S WORK
        </motion.h2>

        <div className="contact-grid">
          <motion.div
            className="contact-info"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="contact-item">
              <span className="contact-label">EMAIL</span>
              <a href="mailto:shayawebdeveloper@gmail.com" className="contact-link">
                shayawebdeveloper@gmail.com
              </a>
            </div>
            <div className="contact-item">
              <span className="contact-label">WHATSAPP</span>
              <a
                href="https://wa.me/447984534823?text=Hi%20Shaya!%20I%20saw%20your%20portfolio%20and%20would%20love%20to%20discuss%20a%20website."
                className="contact-link"
              >
                +44 7984 534823
              </a>

            </div>
          </motion.div>

          <motion.form
            className="contact-form"
            action="https://formspree.io/f/mojdprvd"
            method="POST"
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <input
              type="text"
              name="name"
              placeholder="YOUR NAME"
              required
              className="form-input"
            />
            <input
              type="email"
              name="email"
              placeholder="YOUR EMAIL"
              required
              className="form-input"
            />
            <textarea
              name="message"
              placeholder="YOUR MESSAGE"
              required
              className="form-textarea"
            />
            <button type="submit" className="btn-primary full-width">
              <span>SEND MESSAGE</span>
            </button>
          </motion.form>
        </div>
      </section>

      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@300;400;500;700&family=Roboto+Condensed:wght@300;400;700&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .portfolio-container {
          min-height: 100vh;
          background: #000;
          color: #fff;
          font-family: 'Roboto Condensed', sans-serif;
          position: relative;
          overflow-x: hidden;
        }

        .bg-image {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-image: url('/background.webp');
          background-size: cover;
          background-position: center;
          background-attachment: fixed;
          opacity: 0.9;
          z-index: 0;
        }

        .animated-clouds {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: radial-gradient(ellipse 600px 400px at 50% 50%, rgba(255, 0, 51, 0.3), transparent),
                      radial-gradient(ellipse 400px 300px at 80% 20%, rgba(139, 0, 0, 0.25), transparent),
                      radial-gradient(ellipse 500px 350px at 20% 80%, rgba(178, 34, 34, 0.2), transparent);
          animation: float 20s ease-in-out infinite;
          z-index: 1;
          pointer-events: none;
        }

        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-30px, -30px) scale(1.05); }
          66% { transform: translate(30px, -20px) scale(0.95); }
        }

        .dark-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.75);
          z-index: 2;
          pointer-events: none;
        }

        section {
          position: relative;
          z-index: 3;
          padding: 4rem 2rem;
        }

        .hero-section {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 2rem 1.5rem;
        }

        .hero-content {
          max-width: 900px;
          width: 100%;
        }

        .hero-title {
          font-size: clamp(2.2rem, 6vw, 4.5rem);
          font-weight: 700;
          letter-spacing: -2px;
          font-family: 'Oswald', sans-serif;
          text-transform: uppercase;
          margin-bottom: 3rem;
          line-height: 1.1;
          text-shadow: 0 0 60px rgba(255, 0, 51, 0.8);
        }

        .separator {
          color: #ff0033;
          margin: 0 0.5rem;
          display: inline-block;
        }

        .craft-text {
          display: inline-block;
        }

        .hero-buttons {
          display: flex;
          gap: 1.5rem;
          justify-content: center;
          align-items: center;
          flex-wrap: wrap;
        }

        .btn-primary, .btn-secondary {
          padding: 1.2rem 2.5rem;
          font-size: 1rem;
          font-weight: 700;
          letter-spacing: 2px;
          border: none;
          cursor: pointer;
          font-family: 'Oswald', sans-serif;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          text-transform: uppercase;
          min-width: 200px;
          touch-action: manipulation;
          -webkit-tap-highlight-color: transparent;
        }

        .btn-primary {
          background: linear-gradient(135deg, #ff0033, #cc0028);
          color: #fff;
          clip-path: polygon(15px 0, 100% 0, calc(100% - 15px) 100%, 0 100%);
          box-shadow: 0 0 40px rgba(255, 0, 51, 0.5);
        }

        .btn-primary:hover {
          box-shadow: 0 0 70px rgba(255, 0, 51, 1);
          transform: translateY(-5px);
        }

        .btn-primary:active {
          transform: translateY(-2px);
        }

        .btn-secondary {
          background: black;
          color: #fff;
          border: 2px solid #ff0033;
          clip-path: polygon(15px 0, 100% 0, calc(100% - 15px) 100%, 0 100%);
        }

        .btn-secondary:hover {
          background: rgba(255, 0, 51, 0.2);
          box-shadow: 0 0 40px rgba(255, 0, 51, 0.4);
          transform: translateY(-5px);
        }

        .btn-secondary:active {
          transform: translateY(-2px);
        }

        .about-section {
          padding: 6rem 2rem;
        }

        .about-container {
          max-width: 900px;
          margin: 0 auto;
          background: rgba(0, 0, 0, 0.85);
          border: 2px solid rgba(255, 0, 51, 0.4);
          padding: 3rem 4rem;
          backdrop-filter: blur(15px);
          clip-path: polygon(40px 0, 100% 0, calc(100% - 40px) 100%, 0 100%);
          box-shadow: 0 0 50px rgba(255, 0, 51, 0.3);
        }

        .about-label, .section-label {
          font-size: 0.8rem;
          color: #ff0033;
          letter-spacing: 4px;
          font-weight: 700;
          margin-bottom: 1.5rem;
          font-family: 'Oswald', sans-serif;
        }

        .about-title, .section-title {
          font-size: clamp(2.5rem, 8vw, 4rem);
          font-weight: 700;
          letter-spacing: -3px;
          font-family: 'Oswald', sans-serif;
          text-transform: uppercase;
          margin-bottom: 2.5rem;
          text-shadow: 0 0 40px rgba(255, 0, 51, 0.7);
        }

        .blood-text {
          color: #ff0033;
          display: inline-block;
        }

        .about-content {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .about-content p {
          font-size: clamp(1rem, 2.5vw, 1.15rem);
          line-height: 1.8;
          color: rgba(255, 255, 255, 0.85);
        }

        .about-content strong {
          color: #ff0033;
          font-weight: 700;
        }

        .projects-section {
          padding: 6rem 2rem;
        }

        .section-label {
          text-align: center;
        }

        .section-title {
          text-align: center;
        }

        .projects-grid {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 3rem;
          margin-top: 4rem;
        }

        .services-section {
          padding: 6rem 2rem;
        }

        .services-grid {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2.5rem;
          margin-top: 4rem;
        }

        .service-card {
          background: rgba(0, 0, 0, 0.85);
          border: 2px solid rgba(255, 0, 51, 0.4);
          padding: 3rem 2rem;
          text-align: center;
          transition: all 0.3s ease;
          position: relative;
          backdrop-filter: blur(15px);
          clip-path: polygon(25px 0, 100% 0, calc(100% - 25px) 100%, 0 100%);
          box-shadow: 0 0 40px rgba(255, 0, 51, 0.3);
        }

        .service-card:hover {
          border-color: #ff0033;
          box-shadow: 0 0 70px rgba(255, 0, 51, 0.6);
        }

        .service-icon {
          font-size: 2.5rem;
          color: #ff0033;
          margin-bottom: 1.5rem;
        }

        .service-card h3 {
          font-size: 1.3rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          font-family: 'Oswald', sans-serif;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .service-price {
          font-size: 1.8rem;
          color: #ff0033;
          font-weight: 700;
          font-family: 'Oswald', sans-serif;
        }

        .service-slash {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 100%;
          height: 3px;
          background: linear-gradient(to right, transparent, #ff0033);
        }

        .contact-section {
          padding: 6rem 2rem;
        }

        .contact-grid {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 4rem;
          margin-top: 4rem;
        }

        .contact-info {
          display: flex;
          flex-direction: column;
          gap: 3rem;
        }

        .contact-item {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .contact-label {
          font-size: 0.8rem;
          color: #ff0033;
          letter-spacing: 3px;
          font-weight: 700;
          font-family: 'Oswald', sans-serif;
        }

        .contact-link {
          font-size: clamp(1.1rem, 3vw, 1.5rem);
          color: #fff;
          text-decoration: none;
          font-weight: 700;
          transition: all 0.3s ease;
          font-family: 'Oswald', sans-serif;
          word-break: break-word;
          -webkit-tap-highlight-color: transparent;
        }

        .contact-link:hover {
          color: #ff0033;
          text-shadow: 0 0 30px rgba(255, 0, 51, 1);
          transform: translateX(10px);
        }

        .contact-link:active {
          transform: translateX(5px);
        }

        .contact-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .form-input, .form-textarea {
          padding: 1.3rem;
          background: rgba(0, 0, 0, 0.85);
          border: 2px solid rgba(255, 0, 51, 0.4);
          color: #fff;
          font-size: 1rem;
          font-family: 'Roboto Condensed', sans-serif;
          backdrop-filter: blur(15px);
          transition: all 0.3s ease;
          clip-path: polygon(10px 0, 100% 0, calc(100% - 10px) 100%, 0 100%);
          -webkit-tap-highlight-color: transparent;
        }

        .form-input::placeholder, .form-textarea::placeholder {
          color: rgba(255, 255, 255, 0.4);
          letter-spacing: 1px;
        }

        .form-input:focus, .form-textarea:focus {
          outline: none;
          border-color: #ff0033;
          box-shadow: 0 0 40px rgba(255, 0, 51, 0.5);
          background: rgba(0, 0, 0, 0.9);
        }

        .form-textarea {
          resize: vertical;
          min-height: 150px;
        }

        .full-width {
          width: 100%;
          justify-content: center;
        }

        /* Enhanced Mobile Responsiveness */
        @media (max-width: 768px) {
          section {
            padding: 3rem 1.25rem;
          }

          .hero-section {
            padding: 2rem 1.25rem;
          }

          .hero-title {
            margin-bottom: 2rem;
          }

          .separator {
            display: block;
            margin: 0.5rem 0;
          }

          .craft-text {
            display: block;
          }

          .hero-buttons {
            flex-direction: column;
            width: 100%;
            gap: 1rem;
          }

          .btn-primary, .btn-secondary {
            width: 100%;
            min-width: unset;
            padding: 1.1rem 2rem;
          }

          .about-section {
            padding: 4rem 1.25rem;
          }

          .about-container {
            padding: 2rem 2rem;
            clip-path: polygon(20px 0, 100% 0, calc(100% - 20px) 100%, 0 100%);
          }

          .about-title, .section-title {
            margin-bottom: 2rem;
          }

          .projects-section, .services-section, .contact-section {
            padding: 4rem 1.25rem;
          }

          .projects-grid {
            grid-template-columns: 1fr;
            gap: 2.5rem;
            margin-top: 3rem;
          }

          .services-grid {
            grid-template-columns: 1fr;
            gap: 2rem;
            margin-top: 3rem;
          }

          .service-card {
            padding: 2.5rem 1.5rem;
          }

          .contact-grid {
            grid-template-columns: 1fr;
            gap: 3rem;
            margin-top: 3rem;
          }

          .contact-info {
            gap: 2.5rem;
          }

          .form-input, .form-textarea {
            padding: 1.1rem;
          }
        }

        @media (max-width: 480px) {
          section {
            padding: 2.5rem 1rem;
          }

          .hero-section {
            padding: 1.5rem 1rem;
          }

          .about-container {
            padding: 1.5rem 1.5rem;
          }

          .about-content p {
            font-size: 1rem;
          }

          .projects-grid, .services-grid {
            gap: 2rem;
          }

          .service-card {
            padding: 2rem 1.25rem;
          }

          .contact-grid {
            gap: 2.5rem;
          }

          .form-input, .form-textarea {
            padding: 1rem;
          }
        }

        /* Landscape orientation optimization */
        @media (max-height: 600px) and (orientation: landscape) {
          .hero-section {
            min-height: auto;
            padding: 3rem 1.5rem;
          }

          .hero-title {
            margin-bottom: 1.5rem;
          }

          .hero-buttons {
            margin-top: 1.5rem;
          }
        }

        /* Touch-friendly enhancements */
        @media (hover: none) and (pointer: coarse) {
          .btn-primary:hover,
          .btn-secondary:hover,
          .contact-link:hover,
          .service-card:hover {
            transform: none;
          }

          .btn-primary:active {
            transform: scale(0.98);
          }

          .btn-secondary:active {
            transform: scale(0.98);
          }

          .contact-link:active {
            color: #ff0033;
          }
        }
      `}</style>
    </div>
  );
}


function ProjectCard({ project, index }) {
  const [isHovered, setIsHovered] = useState(false);
  const isDisabled = !!project.disabled;

  // if disabled -> render a div, else -> render an anchor
  const Wrapper = isDisabled ? motion.div : motion.a;

  return (
    <Wrapper
      href={isDisabled ? undefined : project.link}
      className={`project-card ${isDisabled ? "is-disabled" : ""}`}
      aria-disabled={isDisabled ? "true" : undefined}
      role={isDisabled ? "link" : undefined}
      tabIndex={isDisabled ? -1 : undefined}
      initial={{ opacity: 0, y: 80 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay: index * 0.2 }}
      onMouseEnter={() => !isDisabled && setIsHovered(true)}
      onMouseLeave={() => !isDisabled && setIsHovered(false)}
      whileHover={isDisabled ? undefined : { y: -15, scale: 1.02 }}
      style={isDisabled ? { cursor: "not-allowed" } : undefined}
      onClick={(e) => {
        if (isDisabled) e.preventDefault?.();
      }}
    >
      <div className="project-image-wrapper">
        <motion.img
          src={project.image}
          alt={project.title}
          className="project-image"
          style={{ objectPosition: project.imagePosition }}
          animate={{
            scale: isDisabled ? 1 : (isHovered ? 1.2 : 1),
            filter: isDisabled
              ? "grayscale(85%)"
              : (isHovered ? "grayscale(0%)" : "grayscale(60%)"),
          }}
          transition={{ duration: 0.5 }}
        />

        {isDisabled && <div className="badge-soon">IN PROGRESS</div>}
      </div>

      <div className="project-content">
        <h3 className="project-title">{project.title}</h3>
        <p className="project-description">{project.description}</p>
        <p className="project-tech">{project.tech}</p>

        <div className="project-outcome">
          <span className="outcome-label">{isDisabled ? "STATUS" : "RESULT"}</span>
          <span>{project.outcome}</span>
        </div>
      </div>

      <style jsx>{`
        .project-card {
          background: rgba(0, 0, 0, 0.85);
          border: 2px solid rgba(255, 0, 51, 0.4);
          overflow: hidden;
          text-decoration: none;
          color: inherit;
          display: block;
          transition: all 0.3s ease;
          position: relative;
          clip-path: polygon(30px 0, 100% 0, calc(100% - 30px) 100%, 0 100%);
          box-shadow: 0 0 40px rgba(255, 0, 51, 0.3);
          backdrop-filter: blur(15px);
        }

        .project-card:hover {
          border-color: #ff0033;
          box-shadow: 0 0 70px rgba(255, 0, 51, 0.6);
        }

        .project-card.is-disabled {
          opacity: 0.85;
        }

        .project-image-wrapper {
          position: relative;
          width: 100%;
          height: 300px;
          overflow: hidden;
          background: #000;
        }

        .project-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .badge-soon {
          position: absolute;
          top: 16px;
          left: 16px;
          padding: 10px 14px;
          border: 2px solid #ff0033;
          background: rgba(0, 0, 0, 0.75);
          color: #fff;
          font-family: "Oswald", sans-serif;
          font-weight: 700;
          letter-spacing: 2px;
          font-size: 0.75rem;
          z-index: 5;
          clip-path: polygon(10px 0, 100% 0, calc(100% - 10px) 100%, 0 100%);
          box-shadow: 0 0 25px rgba(255, 0, 51, 0.5);
        }

        .project-content {
          padding: 2.5rem;
        }

        .project-title {
          font-size: clamp(1.6rem, 4vw, 2rem);
          font-weight: 700;
          letter-spacing: -1px;
          font-family: "Oswald", sans-serif;
          text-transform: uppercase;
          margin-bottom: 1rem;
        }

        .project-description {
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 1rem;
          font-size: clamp(0.95rem, 2vw, 1.05rem);
          line-height: 1.6;
        }

        .project-tech {
          color: rgba(255, 255, 255, 0.4);
          font-size: 0.85rem;
          margin-bottom: 1.5rem;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .project-outcome {
          background: rgba(255, 0, 51, 0.1);
          padding: 1rem;
          border-left: 3px solid #ff0033;
          font-size: 0.95rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .outcome-label {
          color: #ff0033;
          font-weight: 700;
          font-size: 0.8rem;
          letter-spacing: 2px;
          font-family: "Oswald", sans-serif;
        }

        /* Mobile optimizations for project cards */
        @media (max-width: 768px) {
          .project-card {
            clip-path: polygon(20px 0, 100% 0, calc(100% - 20px) 100%, 0 100%);
          }

          .project-image-wrapper {
            height: 250px;
          }

          .project-content {
            padding: 2rem 1.5rem;
          }
        }

        @media (max-width: 480px) {
          .project-card {
            clip-path: polygon(15px 0, 100% 0, calc(100% - 15px) 100%, 0 100%);
          }

          .project-image-wrapper {
            height: 220px;
          }

          .project-content {
            padding: 1.75rem 1.25rem;
          }

          .badge-soon {
            font-size: 0.7rem;
            padding: 8px 12px;
          }
        }

        /* Touch device optimizations */
        @media (hover: none) and (pointer: coarse) {
          .project-card:hover {
            transform: none;
            border-color: rgba(255, 0, 51, 0.4);
          }

          .project-card:active {
            transform: scale(0.98);
            border-color: #ff0033;
          }
        }
      `}</style>
    </Wrapper>
  );
}